import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Input, Label, Row } from "reactstrap"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import Switch from "components/Shared/Switch"
import { post } from "helpers/api_helper"
import { GLOBAL_SETTINGS } from "helpers/url_helper"
import { updateGlobalSettingByType } from "store/actions/globalSettingAction"
import { showAlert } from "store/reducers/alertReducer"

const TYPE = "deliveryOtpSettings"

// Rider app: above this amount to collect, the rider sends the customer an SMS OTP
// and must enter it to mark the order delivered (service-delivery-partner).
const DeliveryOtpSettingsCard = ({ settingsId, canEdit }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [enabled, setEnabled] = useState(false)
  const [minAmount, setMinAmount] = useState("")
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // Fetched here, not through the store: the shared settings loader toggles the
    // whole page spinner and has no slot for this type.
    post(GLOBAL_SETTINGS, { type: TYPE })
      .then(response => {
        const settings = response?.data?.[TYPE] || {}
        setEnabled(settings.enabled === true)
        setMinAmount(
          settings.minCollectibleAmount != null
            ? String(settings.minCollectibleAmount)
            : ""
        )
      })
      .catch(() => {})
  }, [])

  const validate = () => {
    if (!enabled) return ""
    if (minAmount === "") return t("Please enter the minimum amount.")
    const amount = Number(minAmount)
    if (!Number.isFinite(amount) || amount < 0) {
      return t("Amount must be 0 or more.")
    }
    return ""
  }

  const handleSave = async () => {
    const message = validate()
    setError(message)
    if (message || !settingsId) return

    setSaving(true)
    const response = await dispatch(
      updateGlobalSettingByType({
        id: settingsId,
        type: TYPE,
        [TYPE]: {
          enabled,
          minCollectibleAmount: minAmount === "" ? 0 : Number(minAmount),
        },
      })
    )
    setSaving(false)

    const { status, msg } = response?.payload || {}
    dispatch(
      showAlert({
        text: msg || (status ? t("Saved successfully") : t("Something went wrong")),
        type: status ? "success" : "error",
      })
    )
  }

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <CardTitle>{t("Delivery OTP")}</CardTitle>
            <p className="text-muted">
              {t(
                "When the amount the rider collects from the customer (cash or card on delivery) is equal to or above this amount, the rider sends the customer an OTP by SMS and must enter it to mark the order delivered."
              )}
            </p>
            <Row className="mb-3">
              <Col md={6} className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <Label className="col-form-label mb-0">
                    {t("Require delivery OTP")}
                  </Label>
                  <Switch
                    isChecked={enabled}
                    onClickHandler={() => {
                      setEnabled(!enabled)
                      setError("")
                    }}
                    disabled={!canEdit}
                  />
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <Label htmlFor="deliveryOtpMinAmount">
                  {t("Minimum collectible amount")}
                </Label>
                <Input
                  id="deliveryOtpMinAmount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={minAmount}
                  disabled={!canEdit || !enabled}
                  invalid={Boolean(error)}
                  onChange={e => {
                    setMinAmount(e.target.value)
                    setError("")
                  }}
                />
                {error ? (
                  <div className="invalid-feedback d-block">{error}</div>
                ) : (
                  <small className="text-muted">
                    {t("OTP is required for orders of this amount or more.")}
                  </small>
                )}
              </Col>
            </Row>
            {canEdit ? (
              <div className="d-flex justify-content-end">
                <Button color="primary" onClick={handleSave} disabled={saving}>
                  {saving ? t("Saving...") : t("Save")}
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export default DeliveryOtpSettingsCard
